import { useEffect, useRef, useState } from "react";

import Section from "./Section";

import type { ConfigSection } from "../ConfigSectionHelp";
import type { Agent } from "@/shared/types";

/** How long typing may pause before the text goes to the draft. */
const SAVE_AFTER_MS = 700;

/* The system prompt, written in place: an editor as tall as the page. Typing
   goes to the draft after a pause, and at once on leaving the field, on ⌘S
   (before the Publish bar's own ⌘S publishes it) and on leaving the section. */
export default function InstructionsSection({ agent, onUpdateAgent, onHelp }: {
  agent: Agent;
  onUpdateAgent: (updater: (a: Agent) => Agent) => void;
  onHelp: (section: ConfigSection) => void;
}) {
  const [text, setText] = useState(agent.instructions);
  const [saving, setSaving] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef(text);
  latest.current = text;
  const save = useRef(onUpdateAgent);
  save.current = onUpdateAgent;

  const commit = () => {
    if (timer.current === null) return;
    clearTimeout(timer.current);
    timer.current = null;
    setSaving(false);
    const instructions = latest.current;
    save.current((a) => ({ ...a, instructions }));
  };
  const commitRef = useRef(commit);
  commitRef.current = commit;

  // A draft changed elsewhere (Definable, another tab) shows here, unless an edit is on its way.
  useEffect(() => {
    if (timer.current === null) setText(agent.instructions);
  }, [agent.instructions]);
  useEffect(() => () => commitRef.current(), []);

  const change = (next: string) => {
    setText(next);
    setSaving(true);
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => commitRef.current(), SAVE_AFTER_MS);
  };

  return (
    <Section section="instructions" onHelp={onHelp}>
      <div className="flex flex-col h-[max(360px,calc(100dvh-300px))] rounded-[12px] ring-1 ring-[var(--hairline)] focus-within:ring-[var(--hairline-focus)] bg-[var(--surface)] overflow-hidden transition-shadow">
        <textarea
          value={text}
          onChange={(e) => change(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") commit(); }}
          aria-label="Instructions"
          placeholder="Describe the agent's role, tone, what it should and shouldn't do, and the steps it should follow."
          spellCheck
          className="flex-1 min-h-0 w-full resize-none bg-transparent px-4 py-3.5 outline-none text-[14px] leading-[1.7] text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)] tracking-[-0.005em]"
        />
        <div className="shrink-0 h-10 px-4 flex items-center justify-between gap-3 border-t hairline text-[12px] text-[var(--text-tertiary)]">
          <span><span className="font-mono tabular-nums">{text.length.toLocaleString()}</span> characters</span>
          <span className="whitespace-nowrap">{saving ? "Saving…" : "Saved to the draft"}</span>
        </div>
      </div>
    </Section>
  );
}
